import { MgSupplierItemCard, MgSupplierItemPackage, Unit } from '@retail/products/types';
import TableRow from '@mui/material/TableRow';
import TableCell, { TableCellBaseProps } from '@mui/material/TableCell';
import { CopyToClipboard } from '@retail/components';
import { Box, Stack } from '@mui/material';
import { UnitTooltip } from '../../Units/UnitTooltip';
import Tooltip from '@mui/material/Tooltip';
import { ArmScaleIcon } from '@shared/custom-icons';
import {
  formatPrice,
  getClassCodeLabel,
  getPackageTypeLabel,
  toFixedIfNecessary,
} from '@retail/products/utils';
import { useProductsTFunction } from '@retail/products/i18n';
import { useTranslation } from 'react-i18next';
import { StarIcon } from '@sanity/icons';

interface Props extends MgSupplierItemPackage {
  index: number;
  basePackageUnit?: Unit;
  selectedSupplier: Partial<MgSupplierItemCard> | null;
  mgSupplierName: string;
  mgSupplierId: number;
}

export function MgSupplierItemPackagesTableRow({
  index,
  unit,
  gtinCode,
  grossWeight,
  basePriceUnit,
  priceComparisonUnit,
  calculatedCount,
  basePackageUnit,
  type,
  mgSupplierName,
  mgSupplierId,
  selectedSupplier,
}: Props) {
  const t = useProductsTFunction();
  const { i18n } = useTranslation();

  const unselected = selectedSupplier?.mgSupplier
    ? selectedSupplier.mgSupplier.mgSupplierId !== mgSupplierId
    : false;
  const tableCellProps: Pick<TableCellBaseProps, 'color'> = {
    color: unselected ? 'grey.200' : undefined,
  };

  return (
    <TableRow
      key={calculatedCount}
      sx={{
        backgroundColor: index % 2 === 0 ? 'grey.50' : undefined,
      }}
    >
      <Tooltip title={getPackageTypeLabel(t, type ?? '')}>
        <TableCell sx={{ ...tableCellProps }}>{getClassCodeLabel(type ?? '')}</TableCell>
      </Tooltip>
      <TableCell sx={{ ...tableCellProps }}>{mgSupplierName}</TableCell>

      <TableCell sx={{ ...tableCellProps }}>
        {gtinCode ? (
          <CopyToClipboard
            value={gtinCode}
            CopyIconProps={unselected ? { fontSize: 'small', color: 'disabled' } : undefined}
          >
            {gtinCode}
          </CopyToClipboard>
        ) : (
          '-'
        )}
      </TableCell>

      <TableCell sx={{ ...tableCellProps }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <UnitTooltip {...unit} />
          {!!priceComparisonUnit && (
            <Tooltip title={t('products.mgItem.supplierItem.priceComparisonUnit')}>
              <div>
                <ArmScaleIcon fontSize="small" color={unselected ? 'disabled' : undefined} />
              </div>
            </Tooltip>
          )}
          {!!basePriceUnit && (
            <Tooltip title={t('products.mgItem.supplierItem.basePriceUnit')}>
              <StarIcon fontSize="20px" />
            </Tooltip>
          )}
        </Stack>
      </TableCell>

      <TableCell sx={{ ...tableCellProps }}>
        {unit ? (
          <Box display="flex">
            <UnitTooltip {...unit} />
          </Box>
        ) : (
          '-'
        )}
      </TableCell>

      <TableCell sx={{ ...tableCellProps }}>
        <Stack direction="row" alignItems="center">
          {toFixedIfNecessary(calculatedCount, 2)}

          {basePackageUnit && (
            <UnitTooltip {...basePackageUnit} typographyProps={{ sx: { ml: 1 } }} />
          )}
        </Stack>
      </TableCell>

      <TableCell sx={{ ...tableCellProps, minWidth: 100 }}>
        {grossWeight
          ? `${formatPrice(Number(grossWeight), i18n.language, true)} ${t(
              'products.mgItem.supplierItem.kilo'
            )}`
          : '-'}
      </TableCell>
    </TableRow>
  );
}
